import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { AppRoutes } from '../common/constants';
import { JSX, useState } from 'react';
import { Report, ReportSectionName } from '../common/types/reports';
import { ReportFullStepList, ReportSectionComponentType, ReportSystemSteps } from './types';
import { SanityTemplate } from '../common/types/sanity';
import { useGenerateReportFiles } from '../common/components/mutations';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSanityQuery } from '../common/hooks/useSanityQuery';
import AgreementStep from './steps/agreementStep';
import Content from '../common/components/content';
import CoverPageStep from './steps/coverPageStep';
import DisclosureStep from './steps/disclosureStep';
import ExecutiveSummaryStep from './steps/executiveSummaryStep';
import FnaStep from './steps/fnaStep';
import Layout from '../common/components/layout';
import QuoteStep from './steps/quoteStep';
import RecordOfAdviceStep from './steps/recordOfAdviceStep';
import ReportLanguageSelector, { getReportLanguage } from './components/reportLanguageSelector';
import SelectClientStep from './steps/selectClientStep';
import SelectFnaStep from './steps/selectFnaStep';
import SelectSectionsStep from './steps/selectSectionsStep';
import SlaStep from './steps/slaStep';
import StepHeader from './components/stepHeader';
import StepInstruction from './components/stepInstruction';

const ReportStepComponentsMap: Record<ReportFullStepList, ReportSectionComponentType | null> = {
  [ReportSystemSteps.SelectClient]: SelectClientStep,
  [ReportSystemSteps.SelectFna]: SelectFnaStep,
  [ReportSystemSteps.SelectSections]: SelectSectionsStep,
  [ReportSectionName.CoverPage]: CoverPageStep,
  [ReportSectionName.ExecSummary]: ExecutiveSummaryStep,
  [ReportSectionName.Disclosure]: DisclosureStep,
  [ReportSectionName.Fna]: FnaStep,
  [ReportSectionName.Quote]: QuoteStep,
  [ReportSectionName.SLA]: SlaStep,
  [ReportSectionName.RecordOfAdvice]: RecordOfAdviceStep,
  [ReportSectionName.Agreement]: AgreementStep,
};

const numberOfSystemSteps = Object.values(ReportSystemSteps).length;
const allSectionNames = Object.values(ReportSectionName);

const getAvailableSteps = (report: Report): ReportFullStepList[] => [
  ReportSystemSteps.SelectClient,
  ReportSystemSteps.SelectFna,
  ReportSystemSteps.SelectSections,
  ...report.sections
    .map(({ sectionName }) => sectionName)
    // Keep the same order as in the allSectionNames array in case if report sections are not sorted
    .sort((a, b) => allSectionNames.indexOf(a) - allSectionNames.indexOf(b)),
];

/**
 * Page for editing a report
 */
const EditReport = (): JSX.Element | null => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useGenerateReportFiles();
  const [activeStep, setActiveStep] = useState<ReportFullStepList>(ReportSystemSteps.SelectClient);
  const { data: report } = useQuery<Report>({
    queryKey: [`/ReportDetail/${id}`],
  });
  const reportLanguage = report ? getReportLanguage(report) : 'en';
  // Fetch section template from Sanity to decide what kind of section is it (automatic or manual)
  const { data: categoryTemplates } = useSanityQuery<SanityTemplate[]>({
    queryKey: [
      `*[!(_id in path("drafts.**")) && category->title == "${activeStep}" && _type == "Template" && __i18n_lang == "${reportLanguage}"]{
        ...,
        "fileUrl": file.asset->url
      }`,
    ],
    enabled: Boolean(activeStep),
  });

  const categoryTemplate = categoryTemplates?.[0];

  if (!report) {
    return null;
  }

  const availableSteps = getAvailableSteps(report);
  const StepComponent = ReportStepComponentsMap[activeStep];

  const handleClickBack = (): void => {
    const currentIndex = availableSteps.indexOf(activeStep);
    if (currentIndex === -1) return;
    const requestedStep = availableSteps[currentIndex - 1];
    if (!requestedStep) return;
    setActiveStep(requestedStep as ReportFullStepList);
  };

  // TODO: refactor this peace of art
  const handleClickNext = (updatedReport?: Report): void => {
    const reportSteps = getAvailableSteps(updatedReport || report);
    const currentIndex = reportSteps.indexOf(activeStep);
    if (currentIndex === reportSteps.length - 1) {
      if (!isPending) {
        // Start generating report files and redirect to the results page
        mutateAsync(report.id).then(() => {
          navigate(`${AppRoutes.Reports}/${report.id}/results`);
        });
      }
    } else if (currentIndex >= 0) {
      setActiveStep(reportSteps[currentIndex + 1] as ReportFullStepList);
    }
  };

  const currentSectionIndex = availableSteps.indexOf(activeStep);

  const breadcrumbs = [
    { label: 'Reports', href: AppRoutes.Reports },
    { label: 'Report Builder', href: `${AppRoutes.Reports}/${report.id}` },
  ];

  // Exclude system steps from the step number
  const stepNumberToDisplay = currentSectionIndex - numberOfSystemSteps + 1;

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Box className="flex items-center justify-between mb-4">
          <Typography variant="h2">Report builder</Typography>
          <Box className="flex space-x-4 items-end">
            <ReportLanguageSelector report={report} />
            <Button
              variant="contained"
              color="secondary"
              onClick={(): void => navigate(AppRoutes.Reports)}
            >
              Close
            </Button>
          </Box>
        </Box>
        <StepHeader availableSections={availableSteps} sectionName={activeStep} />
        <Card>
          <CardContent>
            <Box className="mb-4">
              <Typography variant="h3">
                {stepNumberToDisplay > 0 && `Step ${stepNumberToDisplay}: `}
                {activeStep}
              </Typography>
            </Box>
            <StepInstruction sectionName={activeStep} />
            {StepComponent ? (
              <StepComponent
                isLoading={isPending}
                templateFileUrl={categoryTemplate?.fileUrl}
                report={report}
                sectionName={activeStep}
                availableSections={availableSteps}
                onClickBack={handleClickBack}
                onClickNext={handleClickNext}
              />
            ) : (
              <Alert severity="error">Unknown report step</Alert>
            )}
          </CardContent>
        </Card>
      </Content>
    </Layout>
  );
};
export default EditReport;
